import { Component, Input, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
// Share feature: commented out for now. Uncomment to re-enable.
// import { ShareModalComponent } from '../share-modal/share-modal.component';

export interface AssetHeaderConfig {
  breadcrumbs?: { label: string; route?: string }[];
  title: string;
  description: string;
  /** When set (e.g. discussion header), shown as "Created at {date}" with smaller font */
  createdAt?: string;
  iconPath?: string; // Path to decorative icons (e.g., leaf icons)
  assetId?: string; // Asset ID to copy
}

@Component({
  selector: 'app-asset-header',
  standalone: true,
  imports: [CommonModule, RouterModule /* , ShareModalComponent */], // Share feature: uncomment to re-enable
  templateUrl: './asset-header.component.html',
  styleUrl: './asset-header.component.scss',
})
export class AssetHeaderComponent {
  private cdr = inject(ChangeDetectorRef);
  @Input() config!: AssetHeaderConfig;
  @Input() isLoggedIn: boolean = false;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isPinned: boolean = false;
  @Input() showActions: boolean = false; // Controls visibility of Bookmark/Like/Pin buttons (only for detail pages)
  @Input() showLikeButton: boolean = true; // When false, hide Like button (e.g. challenge and discussion pages)
  @Input() showPinButton: boolean = false; // Pin icon only for discussions
  @Input() showVoteButtons: boolean = false; // Upvote/downvote icons only for discussion detail page
  @Input() showShareButton: boolean = false; // Share icon (opens project-themed share modal) — UI commented out for now
  /** Label for share modal preview meta, e.g. Dataset / Model / Use case. */
  @Input() shareKind = 'Page';
  @Input() userVote: 'up' | 'down' | null = null; // Current user vote state (discussion only)
  @Input() voteCount: number = 0; // Display vote count (discussion only)
  /** When true, like/bookmark/pin/vote actions are disabled (e.g. pending/rejected assets). */
  @Input() actionsDisabled: boolean = false;
  /**
   * When set, controls copy independently of actionsDisabled.
   * When unset, copy follows actionsDisabled (asset detail behaviour).
   */
  @Input() copyDisabled: boolean | null = null;
  /** Tooltip shown when actions are disabled. */
  @Input() actionsDisabledTitle: string = 'Unavailable while asset is under review';
  /**
   * When true, only upvote/downvote are disabled (e.g. rejected discussions).
   * Independent of bookmark/pin/like.
   */
  @Input() voteDisabled: boolean = false;
  /** Tooltip shown when vote actions are disabled. */
  @Input() voteDisabledTitle: string = 'Rejected discussion cannot be upvoted';
  
  @Output() bookmarkClick = new EventEmitter<void>();
  @Output() likeClick = new EventEmitter<void>();
  @Output() pinClick = new EventEmitter<void>();
  @Output() upvoteClick = new EventEmitter<void>();
  @Output() downvoteClick = new EventEmitter<void>();
  
  copySuccess: boolean = false;
  // Share feature: commented out for now. Uncomment to re-enable.
  // showShareModal = false;
  // shareUrl = '';

  get isCopyDisabled(): boolean {
    return this.copyDisabled ?? this.actionsDisabled;
  }

  get isVoteDisabled(): boolean {
    return this.voteDisabled || this.actionsDisabled;
  }

  get voteButtonTitle(): string {
    if (this.voteDisabled) {
      return this.voteDisabledTitle;
    }
    if (this.actionsDisabled) {
      return this.actionsDisabledTitle;
    }
    return this.userVote === 'up' ? 'Remove upvote' : 'Upvote';
  }

  copyAssetId(): void {
    if (this.isCopyDisabled) {
      return;
    }

    if (this.config.assetId) {
      navigator.clipboard.writeText(this.config.assetId).then(() => {
        this.copySuccess = true;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.copySuccess = false;
          this.cdr.detectChanges();
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy asset ID:', err);
      });
    }
  }

  onBookmarkClick(): void {
    if (this.actionsDisabled) {
      return;
    }
    this.bookmarkClick.emit();
  }

  onLikeClick(): void {
    if (this.actionsDisabled) {
      return;
    }
    this.likeClick.emit();
  }

  onPinClick(): void {
    if (this.actionsDisabled) {
      return;
    }
    this.pinClick.emit();
  }

  onUpvoteClick(): void {
    if (this.isVoteDisabled) {
      return;
    }
    this.upvoteClick.emit();
  }

  onDownvoteClick(): void {
    if (this.isVoteDisabled) {
      return;
    }
    this.downvoteClick.emit();
  }

  // Share feature: commented out for now. Uncomment to re-enable.
  // onShareClick(): void {
  //   this.shareUrl = window.location.href;
  //   this.showShareModal = true;
  // }
}

